import { useEffect, useState } from "react";
import api from "../services/api";
import { toast } from "sonner";
import useAuthStore from "../store/authStore";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Loader2, Plus, Building2, MapPin, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import HubDetailDrawer from "../components/founder/HubDetailDrawer";

export default function CEODashboard() {
  const { user } = useAuthStore();
  const [overview, setOverview] = useState(null);
  const [mainHubs, setMainHubs] = useState([]);
  const [eligibleMgrs, setEligibleMgrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: "", city: "", address: "", lat: "", lng: "", capacity: 500, manager_id: "" });
  const [saving, setSaving] = useState(false);
  const [assignOpen, setAssignOpen] = useState(null);

  const canWrite = ["founder", "ceo", "admin"].includes(user?.role);
  const navigate = useNavigate();
  const [openHub, setOpenHub] = useState(null);

  const load = async () => {
    try {
      const [{ data: ov }, { data: hb }, { data: el }] = await Promise.all([
        api.get("/hierarchy/exec/overview"),
        api.get("/hierarchy/hubs", { params: { hub_type: "main" } }),
        api.get("/hierarchy/users/eligible", { params: { role: "main_hub_manager" } }),
      ]);
      setOverview(ov);
      setMainHubs(hb.hubs || []);
      setEligibleMgrs(el.users || []);
    } catch {
      toast.error("Failed to load CEO dashboard");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name || !form.city || !form.address || !form.lat || !form.lng) {
      toast.error("All location fields required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/hierarchy/hubs/main", {
        ...form,
        lat: parseFloat(form.lat),
        lng: parseFloat(form.lng),
        capacity: parseInt(form.capacity) || 500,
        manager_id: form.manager_id || null,
      });
      toast.success("Main hub created");
      setCreateOpen(false);
      setForm({ name: "", city: "", address: "", lat: "", lng: "", capacity: 500, manager_id: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const assign = async (hub_id, user_id) => {
    try {
      await api.post(`/hierarchy/hubs/${hub_id}/assign-manager`, { user_id });
      toast.success("Manager assigned");
      setAssignOpen(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed");
    }
  };

  if (loading) return <div className="p-8 flex items-center gap-3"><Loader2 className="animate-spin h-6 w-6 text-orange-500" /> Loading...</div>;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="ceo-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3"><Building2 className="h-7 w-7 text-orange-500" /> Central Operations</h1>
          <p className="text-slate-500 mt-1">Approve & create Main Hubs · assign Main Hub Managers · monitor expansion.</p>
        </div>
        {canWrite && (
          <Button onClick={() => setCreateOpen(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="ceo-create-main-hub-btn">
            <Plus className="h-4 w-4 mr-2" /> New Main Hub
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Kpi label="Main Hubs"   value={overview?.hubs?.main ?? 0}
             onClick={() => navigate("/dashboard/founder/main-hubs")}
             testid="kpi-card-main-hubs"/>
        <Kpi label="Micro Hubs"  value={overview?.hubs?.micro ?? 0}
             onClick={() => navigate("/dashboard/founder/micro-hubs")}
             testid="kpi-card-micro-hubs"/>
        <Kpi label="Campus Hubs" value={overview?.hubs?.campus ?? 0}
             onClick={() => navigate("/dashboard/founder/campus-hubs")}
             testid="kpi-card-campus-hubs"/>
        <Kpi label="Total Orders" value={overview?.orders?.total ?? 0}
             onClick={() => navigate("/dashboard/orders")}
             testid="kpi-card-total-orders"/>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <h3 className="font-semibold text-slate-800 mb-3">Main Hubs · {mainHubs.length}</h3>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Hub</TableHead>
              <TableHead>City</TableHead>
              <TableHead>Manager</TableHead>
              <TableHead className="text-right">Micro Hubs</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mainHubs.map((h) => (
              <TableRow key={h.hub_id} data-testid={`ceo-hub-row-${h.hub_id}`}
                        onClick={() => setOpenHub(h.hub_id)}
                        className="cursor-pointer hover:bg-orange-50/40">
                <TableCell className="font-medium">
                  <button onClick={(e) => { e.stopPropagation(); setOpenHub(h.hub_id); }}
                          data-testid={`ceo-hub-name-${h.hub_id}`}
                          className="text-orange-600 hover:underline text-left">
                    {h.name}
                  </button>
                  <div className="text-xs text-slate-400">{h.hub_id}</div>
                </TableCell>
                <TableCell>{h.city}</TableCell>
                <TableCell>
                  {h.manager ? (
                    <span className="text-sm">{h.manager.name}<br /><span className="text-xs text-slate-400">{h.manager.email}</span></span>
                  ) : (
                    <span className="text-slate-400 italic">Unassigned</span>
                  )}
                </TableCell>
                <TableCell className="text-right">{h.child_count}</TableCell>
                <TableCell className="text-right">
                  {canWrite && (
                    <Button size="sm" variant="outline"
                            onClick={(e) => { e.stopPropagation(); setAssignOpen(h); }}
                            data-testid={`ceo-assign-${h.hub_id}`}>
                      Assign
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {mainHubs.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">No Main Hubs yet — create the first one.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Main Hub</DialogTitle>
            <DialogDescription>City-level hub. Add micro hubs under it later.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Hub name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Mumbai Central Hub" data-testid="ceo-form-name" /></div>
            <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="ceo-form-city" /></div>
            <div><Label>Capacity</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></div>
            <div className="col-span-2"><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="ceo-form-address" /></div>
            <div><Label>Latitude</Label><Input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} data-testid="ceo-form-lat" /></div>
            <div><Label>Longitude</Label><Input value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} data-testid="ceo-form-lng" /></div>
            <div className="col-span-2">
              <Label>Manager (optional)</Label>
              <select className="w-full border rounded-lg p-2" value={form.manager_id} onChange={(e) => setForm({ ...form, manager_id: e.target.value })} data-testid="ceo-form-manager">
                <option value="">— Unassigned —</option>
                {eligibleMgrs.map((u) => <option key={u._id} value={u._id}>{u.name} ({u.email})</option>)}
              </select>
              <p className="text-xs text-slate-400 mt-1">Only users already in the &apos;main_hub_manager&apos; role appear here. Founder can promote first.</p>
            </div>
          </div>
          <Button onClick={submit} disabled={saving} className="mt-3 w-full bg-orange-500" data-testid="ceo-form-submit">
            {saving ? <Loader2 className="animate-spin h-4 w-4" /> : "Create Main Hub"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignOpen} onOpenChange={(o) => !o && setAssignOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Manager — {assignOpen?.name}</DialogTitle>
            <DialogDescription>Pick an eligible main_hub_manager user.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-auto">
            {eligibleMgrs.length === 0 && <p className="text-sm text-slate-400">No eligible managers. Founder must promote a user to &apos;main_hub_manager&apos; first.</p>}
            {eligibleMgrs.map((u) => (
              <div key={u._id} className="flex items-center justify-between border rounded-lg p-3">
                <div>
                  <div className="font-medium">{u.name}</div>
                  <div className="text-xs text-slate-500">{u.email}</div>
                </div>
                <Button size="sm" onClick={() => assign(assignOpen.hub_id, u._id)}>Assign</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Phase HUB-DRILL-1 — main hub drill-down drawer */}
      <HubDetailDrawer hubId={openHub} open={!!openHub} onClose={() => setOpenHub(null)}/>
    </div>
  );
}

function Kpi({ label, value, onClick, testid }) {
  if (onClick) {
    return (
      <button onClick={onClick}
              data-testid={testid}
              className="text-left bg-white rounded-2xl border border-slate-100 p-5 shadow-sm transition hover:border-[#FF6B00] hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6B00] cursor-pointer">
        <div className="text-sm text-slate-500 inline-flex items-center gap-1">{label} <span className="text-[10px] text-[#FF6B00]">→</span></div>
        <div className="text-3xl font-bold mt-1">{value}</div>
      </button>
    );
  }
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm" data-testid={testid}>
      <div className="text-sm text-slate-500">{label}</div>
      <div className="text-3xl font-bold mt-1">{value}</div>
    </div>
  );
}
